import React, { useContext, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import styles from './shop.module.css';
import { ReactComponent as ProIcon } from '../assets/icons/ic_action_osmand_pro_logo_colored.svg';
import PrimaryBtn from '../frame/components/btns/PrimaryBtn';
import GrayBtnWithBlueHover from '../frame/components/btns/GrayBtnWithBlueHover';
import { useTranslation } from 'react-i18next';
import LoginContext from '../context/LoginContext';
import AppContext from '../context/AppContext';
import { createFastSpringPurchase } from '../login/fs/FastSpringHelper';
import { useNavigate } from 'react-router-dom';
import { findPurchase, PRODUCT_ID_PRO, PURCHASE_TYPE_ANNUAL, PURCHASE_TYPE_MONTHLY } from './products/ProductManager';
import { findFeature } from './features/FeaturesManager';
import useCardPriceRefresh from '../util/hooks/useCardPriceRefresh';
import StickyBarContainer from './StickyBarContainer';

export default function StickyBarPaywall({
    visible,
    testMode,
    updateCardPrices,
    setUpdateCardPrices,
    featureId,
    onSeeAllPlans,
}) {
    const ctx = useContext(AppContext);
    const ltx = useContext(LoginContext);

    const { t } = useTranslation();

    const navigate = useNavigate();

    const [monthlyPurchase, setMonthlyPurchase] = useState(null);
    const [annualPurchase, setAnnualPurchase] = useState(null);

    useEffect(() => {
        loadPrices();
    }, []);

    useCardPriceRefresh(updateCardPrices, setUpdateCardPrices, loadPrices);

    function loadPrices() {
        const monthly = findPurchase(PURCHASE_TYPE_MONTHLY, PRODUCT_ID_PRO);
        const annual = findPurchase(PURCHASE_TYPE_ANNUAL, PRODUCT_ID_PRO);
        setMonthlyPurchase(monthly && { ...monthly });
        setAnnualPurchase(annual && { ...annual });
    }

    const feature = findFeature(featureId);

    function subscribeAnnual() {
        if (!ltx.loginUser) {
            ltx.setOpenLoginDialog(true);
        } else if (annualPurchase) {
            createFastSpringPurchase({
                testMode,
                ltx,
                ctx,
                productId: PRODUCT_ID_PRO,
                type: PURCHASE_TYPE_ANNUAL,
                navigate,
            });
        }
    }

    return (
        <StickyBarContainer visible={visible}>
            <Box className={styles.stickyBarPaywallItem}>
                <Box className={styles.stickyBarPaywallItemContent}>
                    <Box className={`${styles.stickyBarIconFrame} ${styles.stickyBarIconFramePro}`}>
                        <ProIcon />
                    </Box>
                    <Box className={styles.stickyBarInfo}>
                        <Typography className={styles.stickyBarName}>OsmAnd Pro</Typography>
                        <Typography className={styles.stickyBarPaywallDesc}>
                            {t(feature?.name ?? 'web:feature_cross_platform')}
                        </Typography>
                    </Box>
                    <Box className={styles.stickyBarPaywallPrices}>
                        <Box className={styles.stickyBarPaywallPriceCol}>
                            <Typography className={styles.stickyBarPaywallPriceLabel}>
                                {t('web:purchase_type_monthly_subscription')}
                            </Typography>
                            <Box className={styles.stickyBarPriceRow}>
                                {monthlyPurchase?.display && (
                                    <Typography className={styles.stickyBarNewPrice}>
                                        {monthlyPurchase.display}
                                    </Typography>
                                )}
                                {monthlyPurchase?.display && (
                                    <Typography className={styles.stickyBarPricePeriod}>
                                        &nbsp;/ {t('web:purchase_period_month')}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                        <Box className={styles.stickyBarPaywallDivider} />
                        <Box className={styles.stickyBarPaywallPriceCol}>
                            <Typography className={styles.stickyBarPaywallPriceLabel}>
                                {t('web:purchase_type_annual_subscription')}
                            </Typography>
                            <Box className={styles.stickyBarPriceRow}>
                                {annualPurchase?.display && (
                                    <Typography className={styles.stickyBarNewPrice}>
                                        {annualPurchase.display}
                                    </Typography>
                                )}
                                {annualPurchase?.display && (
                                    <Typography className={styles.stickyBarPricePeriod}>
                                        &nbsp;/ {t('web:purchase_period_year')}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    </Box>
                </Box>
                <Box className={styles.stickyBarPaywallBtns}>
                    {(testMode || annualPurchase?.show !== false) && (
                        <Box className={styles.stickyBarBtnWrap}>
                            <PrimaryBtn text={t('web:action_subscribe_annual')} span={true} action={subscribeAnnual} />
                        </Box>
                    )}
                    <Box className={styles.stickyBarBtnWrap}>
                        <GrayBtnWithBlueHover text={t('web:action_see_all_plans')} span={true} action={onSeeAllPlans} />
                    </Box>
                </Box>
            </Box>
        </StickyBarContainer>
    );
}
